import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Lightbulb, Check, AlertTriangle, CalendarClock, HardDrive, ShieldAlert, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const ICON = { ai_request: Lightbulb, ai_overdue: AlertTriangle, ai_weekly_summary: CalendarClock, storage_warning: HardDrive };
const LOC = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };

function relTime(iso, lang) {
  try {
    const diff = (new Date(iso).getTime() - Date.now()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(LOC[lang] || "de-CH", { numeric: "auto" });
    const abs = Math.abs(diff);
    if (abs < 60) return rtf.format(Math.round(diff), "second");
    if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
    if (abs < 604800) return rtf.format(Math.round(diff / 86400), "day");
    return new Date(iso).toLocaleDateString(LOC[lang] || "de-CH", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function AdminNotificationBell() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [twofaPending, setTwofaPending] = useState(0);
  const [items, setItems] = useState(null);
  const ref = useRef(null);

  const loadCount = useCallback(() => {
    api.get("/admin/notifications/unread-count").then((r) => { setCount(r.data.count || 0); setOverdue(r.data.overdue || 0); setTwofaPending(r.data.twofa_pending || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const iv = setInterval(loadCount, 30000);
    return () => clearInterval(iv);
  }, [loadCount]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const toggle = (e) => {
    if (e) e.stopPropagation();
    const nx = !open;
    setOpen(nx);
    if (nx) api.get("/admin/notifications").then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/admin/notifications/${n.id}/read`); } catch {}
      loadCount();
    }
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    try { await api.post("/admin/notifications/read-all"); } catch {}
    setItems((prev) => (prev || []).map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="admin-notif-bell"
        className="relative w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
        <Bell className={`w-[18px] h-[18px] ${overdue > 0 ? "text-red-300" : "text-gray-300"}`} />
        {count > 0 && (
          <span data-testid="admin-notif-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
            style={{ background: "#A3E635", color: "#0A0A0C" }}>
            {count > 9 ? "9+" : count}
          </span>
        )}
        {overdue > 0 && (
          <span data-testid="admin-notif-overdue-count" title={`${overdue} überfällige Kundenwünsche`}
            className="absolute -bottom-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white ring-2 ring-[#0A0A0C] animate-pulse">
            {overdue > 9 ? "9+" : overdue}
          </span>
        )}
        {twofaPending > 0 && count === 0 && overdue === 0 && (
          <span data-testid="admin-notif-twofa-dot" title={t("2FA-Einrichtung ausstehend")}
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[#0A0A0C]" />
        )}
      </button>

      {open && (
        <div data-testid="admin-notif-panel"
          className="absolute right-0 mt-2 w-[340px] max-w-[92vw] rounded-2xl border border-white/10 bg-[#0E0E12] shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <span className="font-bold text-sm">{t("Benachrichtigungen")}</span>
            {(items || []).some((x) => !x.read) && (
              <button onClick={markAll} data-testid="admin-notif-mark-all"
                className="text-xs font-semibold flex items-center gap-1 hover:opacity-80 text-[#A3E635]">
                <Check className="w-3.5 h-3.5" /> {t("Alle als gelesen markieren")}
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {twofaPending > 0 && (
              <button onClick={() => { setOpen(false); nav("/admin/security"); }} data-testid="admin-notif-twofa-reminder"
                className="w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 bg-amber-400/[0.06] hover:bg-amber-400/10 transition-colors">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-amber-400/15">
                  <ShieldAlert className="w-4 h-4 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-amber-200">{t("2FA-Einrichtung ausstehend")}</div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {twofaPending === 1
                      ? t("1 Team-Konto hat den Zwei-Faktor-Login noch nicht eingerichtet.")
                      : t("{n} Team-Konten haben den Zwei-Faktor-Login noch nicht eingerichtet.").replace("{n}", twofaPending)}
                  </p>
                  <span className="text-[11px] font-semibold text-amber-300 inline-flex items-center gap-0.5 mt-0.5">{t("Benutzer & Sicherheit öffnen")} <ChevronRight className="w-3 h-3" /></span>
                </div>
              </button>
            )}
            {items === null ? (
              <div className="p-6 text-center text-sm text-gray-500">…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                <div className="text-sm font-semibold">{t("Keine Benachrichtigungen")}</div>
                <p className="text-xs text-gray-500 mt-1">{t("Sie sind auf dem neusten Stand.")}</p>
              </div>
            ) : (
              items.map((n) => {
                const Icon = ICON[n.type] || Bell;
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`admin-notif-item-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${n.read ? "opacity-60" : ""}`}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#A3E6351A" }}>
                      <Icon className="w-4 h-4 text-[#A3E635]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm truncate">{t(n.title)}</span>
                        {!n.read && <span className="w-2 h-2 rounded-full shrink-0 bg-[#A3E635]" />}
                      </div>
                      {n.ref && <p className="text-xs text-gray-400 truncate mt-0.5">{n.ref}</p>}
                      <span className="text-[11px] text-gray-500">{relTime(n.created_at, lang)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
